import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";

const fmt = (d) =>
  d && dayjs(d).isValid() ? dayjs(d).format("MM/DD/YYYY") : "";

// Dropdown option sets
const PULSE_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "irregular", label: "Irregular" },
  { value: "regularly irregular", label: "Regular Irregular" },
  { value: "irregularly regular", label: "Irregular regular" },
];

const ARM_OPTIONS = [
  { value: "L arm", label: "L arm" },
  { value: "R arm", label: "R arm" },
  { value: "L wrist", label: "L wrist" },
  { value: "R wrist", label: "R wrist" },
  { value: "L Leg", label: "L Leg" },
  { value: "R Leg", label: "R Leg" },
];

const UNIT_OPTIONS = [
  { value: "lbs", label: "lbs" },
  { value: "kg", label: "kg" },
  { value: "stone", label: "stone" },
];

export default function BasicModal({
    systolic,
    finalsave,
    finalload,
  setSystolic,
  handleSave,
  diastolic,
  editTarget,
  setEditTarget,
  setDiastolic,
  arm,
  setArm,
  pulse,
  setPulse,
  pulseType,
  setPulseType,
  weight,
  setWeight,
  weightUnit = "lbs",
  setWeightUnit = () => {},
  heightFt,
  setHeightFt,
  heightIn,
  setHeightIn,

  previousdata = {},
}) {
  const hasPrevHeight = Boolean(
    previousdata?.height_ft || previousdata?.height_in
  );
const [isGlobalEdit, setIsGlobalEdit] = useState(true); // initially edit mode

  const [heightLocked, setHeightLocked] = useState(hasPrevHeight);
const [editWeight, setEditWeight] = useState(false);
const [editHeight, setEditHeight] = useState(false);
const [weightMode, setWeightMode] = useState("view"); // view | edit

const clearAllFields = () => {
  setSystolic("");
  setDiastolic("");
  setArm("");
  setPulse("");
  setPulseType("");
  setWeight("");
  setWeightUnit("lbs");
  setHeightFt("");
  setHeightIn("");
};

const hydrateFromPrevious = () => {
  setSystolic(previousdata?.bp_mm || "");
  setDiastolic(previousdata?.bp_hgg || "");
  setArm(previousdata?.bp_pos || "");
  setPulse(previousdata?.plus_bpm || "");
  setPulseType(previousdata?.plus_drop || "");
  setWeight(previousdata?.weight || "");
  setWeightUnit(previousdata?.weight_cel || "lbs");
  setHeightFt(previousdata?.height_ft || "");
  setHeightIn(previousdata?.height_in || "");
};

  // useEffect(() => {
  //   if (hasPrevHeight) {
  //     setHeightFt(previousdata.height_ft);
  //     setHeightIn(previousdata.height_in);
  //   }
  //   setSystolic(previousdata?.bp_mm||'')
  //   setDiastolic(previousdata?.bp_hgg||'')
  //   setArm(previousdata?.bp_pos||'')
  //   setPulse(previousdata?.plus_bpm||'')
  //   setPulseType(previousdata?.plus_drop)
  //   setWeight(previousdata?.weight||'')
  //   setWeightUnit(previousdata?.weight_cel||'')
  //   setHeightIn(previousdata?.height_in||'')
  //   setHeightFt(previousdata?.height_ft||'')
  // }, [hasPrevHeight]);
  const height = useMemo(() => {
  const ft = parseFloat(heightFt || "0");
  const inches = parseFloat(heightIn || "0");
  const totalInches = ft * 12 + inches;

  return {
    inches: totalInches || 0,
    meters: totalInches ? totalInches * 0.0254 : 0,
  };
}, [heightFt, heightIn]);

const bmi = useMemo(() => {
  const w = parseFloat(weight || "0");
  if (!w || !height.meters) return "";

  const kg =
    weightUnit === "lbs"
      ? w * 0.45359237
      : weightUnit === "stone"
      ? w * 6.35029
      : w;

  return (kg / (height.meters * height.meters)).toFixed(1);
}, [weight, weightUnit, height.meters]);

const calculateBMI = (w, unit, ft, inch) => {
  const weightNum = parseFloat(w);
  const heightInches =
    parseFloat(ft || 0) * 12 + parseFloat(inch || 0);

  if (!weightNum || !heightInches) return "";

  const meters = heightInches * 0.0254;

  const kg =
    unit === "lbs"
      ? weightNum * 0.45359237
      : unit === "stone"
      ? weightNum * 6.35029
      : weightNum;

  return (kg / (meters * meters)).toFixed(1);
};
const bmiFromState = useMemo(
  () => calculateBMI(weight, weightUnit, heightFt, heightIn),
  [weight, weightUnit, heightFt, heightIn]
);

const bmiFromPrevious = useMemo(
  () =>
    calculateBMI(
      previousdata?.weight,
      previousdata?.weight_cel,
      previousdata?.height_ft,
      previousdata?.height_in
    ),
  [previousdata]
);

  return (
    <div className="w-full bg-white p-6">
  <div className="flex items-center gap-2 justify-end mb-2">

  {/* + icon → only in VIEW mode */}
  {!isGlobalEdit && (
    <div
      onClick={() => {
        clearAllFields()
        // hydrateFromPrevious();
        setIsGlobalEdit(true); // open all cards for edit
      }}
      className="h-8 w-8 cursor-pointer rounded-full bg-[#FDE2DA] text-[#E07A5F] text-xl flex items-center justify-center"
    >
      +
    </div>
  )}

  {/* Save button → only in EDIT mode */}
  {isGlobalEdit && (
    <button
      onClick={() => {
        finalsave();       // your existing save
        setIsGlobalEdit(false); // switch to view mode
      }}
      className="rounded-md bg-[#1D2951] px-6 py-1 text-sm font-semibold text-white"
      disabled={finalload}
    >
      Save
    </button>
  )}
</div>

       
      <div className="mx-auto max-w-6xl">
        {/* ✅ THREE CARDS IN ONE ROW */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 text-start">

          {/* ================= BLOOD PRESSURE ================= */}
          <EditableCard title="Blood Pressure"    clearAllFields={clearAllFields} isGlobalEdit={isGlobalEdit} hydrateFromPrevious={   hydrateFromPrevious}>
            {({ isEditing, stopEdit }) =>
              !isEditing ? (
                <>
                  <Meta text={fmt(previousdata?.created_at)} />

            <div className="mt-3 flex items-center gap-2">
  <div className="text-[36px] font-bold text-[#1D2951]">
    {previousdata?.bp_mm && previousdata?.bp_hgg
      ? `${previousdata.bp_mm}/${previousdata.bp_hgg}`
      : "-"}
  </div>

  <span className="rounded bg-slate-100 px-2 py-1 text-sm text-slate-600">
    {previousdata?.bp_pos || "-"}
  </span>
</div>

{/* 
                  <div className="mt-1 text-xs text-slate-400">2:30 PM</div> */}
{(previousdata?.bp1_mm && previousdata?.bp1_hgg) ||
 (previousdata?.bp2_mm && previousdata?.bp2_hgg) ? (
  <div className="mt-6">
    <div className="text-sm font-medium text-slate-600">
      All Readings
    </div>

    <div className="mt-3 space-y-2 text-sm">
      {previousdata?.bp1_mm && previousdata?.bp1_hgg && (
        <div className="flex justify-between">
          <span className="font-medium">
            {previousdata.bp1_mm}/{previousdata.bp1_hgg}
          </span>
          <span className="text-slate-500">
            {previousdata?.bp1_pos || ""}
          </span>
        </div>
      )}

      {previousdata?.bp2_mm && previousdata?.bp2_hgg && (
        <div className="flex justify-between">
          <span className="font-medium">
            {previousdata.bp2_mm}/{previousdata.bp2_hgg}
          </span>
          <span className="text-slate-500">
            {previousdata?.bp2_pos || ""}
          </span>
        </div>
      )}
    </div>
  </div>
) : null}

                </>
              ) : (
                <>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <Label>Systolic</Label>
                      <Input value={systolic} onChange={setSystolic} />
                    </div>
                    <div>
                      <Label>Diastolic</Label>
                      <Input value={diastolic} onChange={setDiastolic} />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Select value={arm} onChange={setArm} options={ARM_OPTIONS} />
                  </div>
{/* 
                  <Actions onSave={()=>
                  {
                    stopEdit()
                    handleSave()
                  }
                    } onCancel={() => {
  hydrateFromPrevious();
  stopEdit();
}}
 /> */}
                </>
              )
            }
          </EditableCard>

          {/* ================= PULSE ================= */}
          <EditableCard title="Pulse" isGlobalEdit={isGlobalEdit}  clearAllFields={clearAllFields} hydrateFromPrevious={   hydrateFromPrevious}>
            {({ isEditing, stopEdit }) =>
              !isEditing ? (
                <>
                  <Meta text={fmt(previousdata?.created_at)} />

               <div className="mt-3 flex items-baseline gap-2">
  <div className="text-[36px] font-bold text-[#1D2951]">
    {previousdata?.plus_bpm ?? "-"}
  </div>
  <span className="text-lg text-slate-500">bpm</span>
</div>


                  <div className="mt-1 text-sm text-green-500">{previousdata?.plus_drop||'-'}</div>

                  {/* <div className="mt-6">
                    <div className="text-sm font-medium text-slate-600">
                      All Readings
                    </div>

                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">84</span>
                        <span className="text-slate-500">Regular · 8:00 AM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">100</span>
                        <span className="text-slate-500">Regular · 7:00 AM</span>
                      </div>
                    </div>
                  </div> */}
                </>
              ) : (
                <>
                  <div className="mt-6">
                    <Label>Rate</Label>
                    <div className="mt-2 relative w-full">
                      <input
                        value={pulse}
                        onChange={(e) => setPulse(e.target.value)}
                        className="h-10 w-full rounded border pl-3 pr-16 bg-white"
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r border bg-[#F9FAFB] px-3 text-sm text-[#6A7282] font-inter">
                        bpm
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Select
                      value={pulseType}
                      onChange={setPulseType}
                      options={PULSE_OPTIONS}
                    />
                  </div>

                  {/* <Actions onSave={()=>
                  {
                    stopEdit()
                    handleSave()
                  }}
                  onCancel={() => {
  hydrateFromPrevious();
  stopEdit();
}}
 /> */}
                </>
              )
            }
          </EditableCard>


{/* ================= WEIGHT / HEIGHT / BMI ================= */}
{!isGlobalEdit&& (
  <div className="relative rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="text-[16px] font-medium text-[#1D2951]">
        Weight, Height & BMI
      </div>

      {/* + → EDIT BOTH
      <button
        onClick={() => {
          // hydrateFromPrevious();
            clearAllFields();  
          setEditTarget(null);       // 👈 edit BOTH
          setWeightMode("edit");
        }}
        className="h-8 w-8 rounded-full bg-[#FDE2DA] text-[#E07A5F] text-xl"
      >
        +
      </button> */}
    </div>

    {/* Weight & BMI */}
    <div className="mt-4 flex justify-between">
      <div>
        <div className="text-sm text-slate-500">Weight</div>
     <div className="mt-1 flex items-end gap-1">
  <div className="text-[28px] font-bold text-[#1D2951]">
    {previousdata?.weight ?? "-"}
  </div>

  {previousdata?.weight != null && (
    <span className="mb-1 text-sm text-slate-500">
      {previousdata?.weight_cel}
    </span>
  )}
</div>

      </div>

      <div className="text-center">
        <div className="text-sm text-slate-500">BMI</div>
        <div className="mt-1 flex h-12 w-[70px] items-center justify-center rounded-lg bg-[#1b2745] text-lg font-bold text-white">
          {bmiFromPrevious || "--"}
        </div>
      </div>
    </div>

    {/* Height */}
    <div className="mt-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        Height
        {/* ✏️ → EDIT BOTH */}
        {/* <button
          onClick={() => {
          clearAllFields();  
            setEditTarget(null);     // 👈 edit BOTH
            setWeightMode("edit");
          }}
        >
          <EditIcon fontSize="small" />
        </button> */}
      </div>

      <div className="mt-2 flex gap-3">
        <input
          disabled
          value={previousdata?.height_ft}
          className="h-10 w-20 rounded bg-slate-200 text-center font-medium"
        />
        <input
          disabled
          value={previousdata?.height_in}
          className="h-10 w-20 rounded bg-slate-200 text-center font-medium"
        />
      </div>
    </div>
  </div>
)}


{/* ================= EDIT MODE ================= */}
{isGlobalEdit&& (
  <div className="rounded-xl border border-[#E5E7EB] bg-white p-6">
    <div className="mb-4 text-[16px] font-medium text-[#1D2951]">
      Weight, Height & BMI
    </div>

    {/* Weight & BMI */}
    <div className="mb-6 flex items-end justify-between">
      <div className="flex-1">
        <Label>Weight</Label>
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={weight}
            onChange={setWeight}
            disabled={false}   // 👈 always editable
          />
          {/* Fixed unit display to match design */}
          <span className="text-sm text-slate-500">lbs</span>
        </div>
      </div>

      <div className="ml-6 w-[110px]">
        <div className="text-sm text-slate-600">BMI</div>
        <div className="mt-2 flex h-10 items-center justify-center rounded border border-[#E5E7EB] bg-white text-base font-semibold text-[#1D2951]">
          {bmiFromState || "--"}
        </div>
      </div>
    </div>

    {/* Height */}
    <div className="mb-6">
      <Label>Height</Label>
      <div className="mt-2 flex gap-3">
        <Input
          value={heightFt}
          onChange={setHeightFt}
          disabled={false}   // 👈 always editable
        />
        <span className="mt-2 text-sm">ft</span>
        <Input
          value={heightIn}
          onChange={setHeightIn}
          disabled={false}   // 👈 always editable
        />
        <span className="mt-2 text-sm">in</span>
      </div>
    </div>

    {/* Actions */}
    {/* <div className="mt-6 flex gap-4">
      <button
        onClick={() => {
          handleSave();
          setWeightMode("view");
          setEditTarget(null);
        }}
        className="rounded bg-[#E07A5F] px-6 py-2 text-white"
      >
        Save
      </button>

      <button
        onClick={() => {
            hydrateFromPrevious();
          setWeightMode("view");
          setEditTarget(null);
        }}
        className="text-[#1D2951]"
      >
        Cancel
      </button>
    </div> */}
  </div>
)}






        </div>
      </div>
    </div>
  );
}

/* ================= REUSABLE COMPONENTS ================= */

const EditableCard = ({ title, children , isGlobalEdit,  hydrateFromPrevious,clearAllFields}) => {
  const [isEditing, setIsEditing] = useState(false);
 useEffect(() => {
    setIsEditing(isGlobalEdit);
  }, [isGlobalEdit]);
  return (
    <div className="relative rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-6">
      <div className="flex justify-between items-center">
        <div className="text-[16px] font-medium text-[#1D2951]">{title}</div>
        {/* {!isEditing && (
          <button
            onClick={() => {
             
                  clearAllFields(); 
              setIsEditing(true)
            }}
            className="h-8 w-8 rounded-full bg-[#FDE2DA] text-[#E07A5F] text-xl"
          >
            +
          </button>
        )} */}
      </div>

      {children({
        isEditing,
        stopEdit: () => setIsEditing(false),
      })}
    </div>
  );
};

const Meta = ({ text }) => (
  <div className="mt-2 text-sm text-slate-500">{text}</div>
);

const Label = ({ children }) => (
  <div className="text-sm text-slate-600">{children}</div>
);

const Input = ({ value, onChange, disabled }) => (
  <input
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    className={`h-10 w-full rounded border px-3 ${
      disabled ? "bg-slate-200 cursor-not-allowed" : "bg-white"
    }`}
  />
);


const Select = ({ value, onChange, options, disabled }) => (
  <select
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    className={`mt-1 h-10 w-full rounded border px-3 ${
      disabled ? "bg-slate-200 cursor-not-allowed" : "bg-white"
    }`}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);


const Actions = ({ onCancel,onSave }) => (
  <div className="mt-6 flex gap-4">
    <button onClick={onSave}  className="rounded bg-[#E07A5F] px-6 py-2 text-white">
      Save
    </button>
    <button onClick={onCancel} className="text-[#1D2951]">
      Cancel
    </button>
  </div>
);
